<template>
  <highlight-card
    :button="{ label: texts.photosCta }"
    :heading="sprintf(bookTexts.heading, deceasedName)"
    :status="status"
    :text="bookTexts.upcomingText"
    @action="addPhoto"
  >
    <template #media>
      <button
        class="book-upcoming-card__scene"
        type="button"
        :aria-label="texts.photosCta"
        @click="addPhoto"
      >
        <book-scene
          :name="cover.name"
          :portrait="cover.portrait"
          :years="cover.years"
          :zoom="zoom"
        />
      </button>
    </template>
  </highlight-card>
</template>

<script lang="ts" setup>
  import { sprintf } from "sprintf-js";
  import { computed } from "vue";
  import { useStore } from "vuex";
  import {
    quickContent,
    useMediaQuery,
  } from "../../../composables/useSlideshow";
  import { MEDIA_INITIAL } from "../../../createCommentStepsHelper";
  import { saleWhen, type UpcomingBook } from "../../../highlights/memory-book";
  import { highlightTexts, memoryBookTexts } from "../../../highlights/texts";
  import BookScene from "../../MemoryBook/BookScene.vue";
  import HighlightCard from "../HighlightCard.vue";

  /**
   * While the family's book is on its way: the book in someone's hands with a cover made from the
   * page, the greetings with photos so far, and, once there are enough for a book, when it can be
   * ordered. The ask is the photos card's.
   */
  const props = defineProps<{
    deceasedName: string;
    /** The block's clock, so "today" and "tomorrow" move on with the day. */
    now?: Date;
    /** IANA zone of the ceremony's calendar days, or '' for the visitor's. */
    timeZone: string;
    upcoming: UpcomingBook;
  }>();

  /** $breakpoint-m: below it the card is a slide of the swipe track, closer on the book. */
  const DESKTOP = "(min-width: 992px)";

  const store = useStore();
  const texts = highlightTexts();
  const bookTexts = memoryBookTexts();
  const content = quickContent();
  const desktop = useMediaQuery(DESKTOP);
  const zoom = computed(() => (desktop.value ? 1.1 : 1.2));

  // The printed cover names them without the nickname.
  const cover = {
    name:
      content?.deceased.formalName ||
      content?.deceased.name ||
      props.deceasedName,
    portrait: content?.deceased.portrait ?? null,
    years: content?.deceased.years ?? "",
  };

  /** About what the book needs (the API's draft line: five photos), said to visitors as greetings. */
  const ENOUGH = 5;

  const withPhotos = (content?.greetings ?? []).filter(
    (g) => g.hasMedia
  ).length;

  function soFar(): string {
    if (withPhotos === 0) {
      return "";
    }
    return withPhotos === 1
      ? bookTexts.upcomingSoFarOne
      : sprintf(bookTexts.upcomingSoFar, withPhotos);
  }

  // No sale day before there is enough for a book: it may never be made.
  function whenOrNeeds(): string {
    if (withPhotos < ENOUGH) {
      return sprintf(bookTexts.upcomingNeeds, ENOUGH);
    }
    return props.upcoming.shareAt
      ? saleWhen(
          props.upcoming.shareAt,
          bookTexts,
          props.timeZone,
          props.now ?? new Date()
        )
      : bookTexts.upcomingLater;
  }

  const status = computed(() =>
    [soFar(), whenOrNeeds()].filter(Boolean).join(" · ")
  );

  function addPhoto() {
    store.dispatch("createComment/toggleModal", MEDIA_INITIAL);
  }
</script>
